import { useCallback, useMemo } from "react";
import { Input } from "./input";
import { StringSelect } from "./string-select";
import { cn } from "../lib/utils";

/**
 * Relative date value representing a duration in the past.
 * @example { $relative: { amount: 30, unit: "days", direction: "ago" } }
 */
export interface RelativeDateValue {
  $relative: {
    amount: number;
    unit: "days" | "weeks" | "months" | "years";
    direction: "ago"; // Extensible for future "fromNow"
  };
}

/**
 * Dynamic date value - either a built-in keyword or a relative date.
 * - "$now": Current timestamp
 * - "$today": Start of the current day
 * - RelativeDateValue: A relative duration (e.g., "30 days ago")
 */
export type DynamicDateValue = "$now" | "$today" | RelativeDateValue;

/**
 * Union type for all possible date values in the input.
 * Can be a dynamic value, a relative value, or a timestamp number.
 */
export type DateValue = DynamicDateValue | number;

/**
 * Date input mode for the component.
 * - relative: "30 days ago"
 * - dynamic: "$now" or "$today"
 */
type DateInputMode = "relative" | "dynamic";

export interface RelativeDateInputProps {
  /** The current date value (dynamic, relative, or timestamp) */
  value: DateValue | null;
  /** Called when the value changes */
  onChange: (value: DateValue) => void;
  /** Additional CSS classes */
  className?: string;
  /** Accessible label for the mode selector */
  modeLabel?: string;
  /** Accessible label for the amount input */
  amountLabel?: string;
  /** Accessible label for the unit selector */
  unitLabel?: string;
  /** Accessible label for the dynamic value selector */
  dynamicLabel?: string;
}

const DATE_UNITS: Array<{ value: RelativeDateValue["$relative"]["unit"]; label: string }> = [
  { value: "days", label: "days ago" },
  { value: "weeks", label: "weeks ago" },
  { value: "months", label: "months ago" },
  { value: "years", label: "years ago" },
];

const DYNAMIC_VALUES: Array<{ value: "$now" | "$today"; label: string }> = [
  { value: "$now", label: "Now (current time)" },
  { value: "$today", label: "Today (start of day)" },
];

/**
 * Determines the input mode from a value.
 */
function getModeFromValue(value: DateValue | null): DateInputMode {
  if (value === "$now" || value === "$today") {
    return "dynamic";
  }
  if (typeof value === "object" && value !== null && "$relative" in value) {
    return "relative";
  }
  // Default to relative for unknown/null values
  return "relative";
}

/**
 * Extracts relative date components from a value.
 */
function getRelativeComponents(value: DateValue | null): {
  amount: number;
  unit: RelativeDateValue["$relative"]["unit"];
} {
  if (typeof value === "object" && value !== null && "$relative" in value) {
    return {
      amount: value.$relative.amount,
      unit: value.$relative.unit,
    };
  }
  // Default values
  return { amount: 30, unit: "days" };
}

/**
 * Creates a relative date value.
 */
function createRelativeValue(amount: number, unit: RelativeDateValue["$relative"]["unit"]): RelativeDateValue {
  return {
    $relative: {
      amount: Math.max(0, amount),
      unit,
      direction: "ago",
    },
  };
}

/**
 * Input component for date comparison values.
 *
 * Supports:
 * - Relative dates: "30 days ago", "1 week ago", etc.
 * - Dynamic values: $now (current timestamp), $today (start of day)
 *
 * @example
 * ```tsx
 * <RelativeDateInput
 *   value={{ $relative: { amount: 30, unit: "days", direction: "ago" } }}
 *   onChange={(value) => console.log(value)}
 * />
 * ```
 *
 * @example
 * ```tsx
 * // With dynamic value
 * <RelativeDateInput
 *   value="$now"
 *   onChange={(value) => console.log(value)}
 * />
 * ```
 */
export function RelativeDateInput({
  value,
  onChange,
  className,
  modeLabel = "Date mode",
  amountLabel = "Amount",
  unitLabel = "Unit",
  dynamicLabel = "Dynamic value",
}: RelativeDateInputProps) {
  const mode = useMemo(() => getModeFromValue(value), [value]);
  const relativeComponents = useMemo(() => getRelativeComponents(value), [value]);

  // Handle mode change
  const handleModeChange = useCallback(
    (newMode: DateInputMode) => {
      if (newMode === "dynamic") {
        // Switch to dynamic - default to $now
        onChange("$now");
      } else {
        // Switch to relative - use previous relative values or defaults
        onChange(createRelativeValue(relativeComponents.amount, relativeComponents.unit));
      }
    },
    [onChange, relativeComponents]
  );

  // Handle dynamic value change
  const handleDynamicChange = useCallback(
    (newValue: "$now" | "$today") => {
      onChange(newValue);
    },
    [onChange]
  );

  // Handle relative amount change
  const handleAmountChange = useCallback(
    (newAmount: string) => {
      const amount = parseInt(newAmount, 10) || 0;
      onChange(createRelativeValue(amount, relativeComponents.unit));
    },
    [onChange, relativeComponents.unit]
  );

  // Handle relative unit change
  const handleUnitChange = useCallback(
    (newUnit: RelativeDateValue["$relative"]["unit"]) => {
      onChange(createRelativeValue(relativeComponents.amount, newUnit));
    },
    [onChange, relativeComponents.amount]
  );

  return (
    <div className={cn("flex items-center gap-1", className)}>
      {/* Mode selector */}
      <StringSelect
        value={mode}
        onValueChange={handleModeChange}
        options={[
          { value: "relative", label: "Relative" },
          { value: "dynamic", label: "Dynamic" },
        ]}
        className="w-[90px] h-8 text-xs"
        aria-label={modeLabel}
      />

      {mode === "dynamic" ? (
        // Dynamic value selector
        <StringSelect
          value={value === "$now" || value === "$today" ? value : "$now"}
          onValueChange={handleDynamicChange}
          options={DYNAMIC_VALUES}
          className="flex-1 h-8 text-xs min-w-[120px]"
          aria-label={dynamicLabel}
        />
      ) : (
        // Relative date inputs
        <>
          <Input
            type="number"
            min={0}
            value={relativeComponents.amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            className="w-[60px] h-8 text-xs"
            aria-label={amountLabel}
          />
          <StringSelect
            value={relativeComponents.unit}
            onValueChange={handleUnitChange}
            options={DATE_UNITS}
            className="w-[100px] h-8 text-xs"
            aria-label={unitLabel}
          />
        </>
      )}
    </div>
  );
}
